import { useEffect, useMemo, useRef, useState } from "react";
import type { PdfSource } from "../types";

type ObjectUrlSource = PdfSource | string | null | undefined;

function normalizeBlob(source: PdfSource): Blob {
  if (source instanceof File) {
    return source;
  }

  const view =
    source instanceof Uint8Array
      ? source
      : new Uint8Array(source as ArrayBufferLike);

  const buffer = new ArrayBuffer(view.byteLength);
  new Uint8Array(buffer).set(view);

  return new Blob([buffer], { type: "application/pdf" });
}

export function useObjectUrl(source: ObjectUrlSource) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const previousUrlRef = useRef<string | null>(null);

  const isStringSource = typeof source === "string";

  const blob = useMemo(() => {
    if (!source || isStringSource) {
      return null;
    }

    return normalizeBlob(source);
  }, [source, isStringSource]);

  useEffect(() => {
    if (isStringSource) {
      setObjectUrl(source as string);
      return;
    }

    if (!blob) {
      setObjectUrl(null);
      return;
    }

    const nextUrl = URL.createObjectURL(blob);
    setObjectUrl(nextUrl);

    if (previousUrlRef.current) {
      URL.revokeObjectURL(previousUrlRef.current);
    }

    previousUrlRef.current = nextUrl;

    return () => {
      if (nextUrl) {
        URL.revokeObjectURL(nextUrl);
      }
      previousUrlRef.current = null;
    };
  }, [blob, source, isStringSource]);

  return objectUrl;
}

